// Task group › Network (D81): what the group's agents reached and what was refused in the chosen
// period, and — for its owner — allowing blocked domains, which adds them to the group's list
// (every group on that list gets them; the confirmation says so).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { accessQuery } from "../auth/access";
import { formatNumber, useT } from "../i18n";
import { detailCrumbs } from "../layout/nav";
import { toast } from "../layout/toast";
import { allowDomains, groupNetworkQuery, networkListQuery } from "../network/api";
import { BlockedTable } from "../network/BlockedTable";
import { DAY_CHOICES, useDays } from "../network/days";
import { NoListCallout } from "../network/NoListCallout";
import { taskGroupQuery } from "../tasks/api";
import { WidgetCard } from "../ui/Card";
import { PageHeader } from "../ui/PageHeader";
import { Segmented } from "../ui/Segmented";
import { ErrorState, Loading } from "../ui/states";
import { Mono } from "../ui/status";

export default function TaskGroupNetworkPage() {
  const { network, shell } = useT();
  const t = network.page;
  const { groupId = "" } = useParams();
  const [days, setDays] = useDays();
  const group = useQuery(taskGroupQuery(groupId));
  const usage = useQuery(groupNetworkQuery(groupId, days));
  const me = useQuery(accessQuery()).data;
  const listId = usage.data?.network_list?.id ?? "";
  const list = useQuery({ ...networkListQuery(listId), enabled: listId !== "" });
  const queryClient = useQueryClient();
  const name = group.data?.name ?? shell.pages.taskGroup.title;
  const crumbs = detailCrumbs("taskGroups", "/task-groups", { name, href: `/task-groups/${groupId}` }, { name: shell.pages.taskGroupNetwork.title });
  const owner = group.data !== undefined && me?.id === group.data.owner_user_id;
  const n = usage.data;

  async function allow(domains: string[]) {
    await allowDomains(groupId, domains);
    await Promise.all([queryClient.invalidateQueries({ queryKey: ["task-groups", groupId, "network"] }), queryClient.invalidateQueries({ queryKey: ["network-lists"] })]);
    toast(t.allowed(domains.length));
  }

  return (
    <>
      <PageHeader title={shell.pages.taskGroupNetwork.title} breadcrumb={crumbs} description={shell.pages.taskGroupNetwork.subtitle}>
        <Segmented label={network.group.window} options={DAY_CHOICES.map((d) => ({ key: d, label: network.group.days(d) }))} value={days} onChange={setDays} />
      </PageHeader>
      {usage.isPending ? <Loading /> : null}
      {usage.isError ? <ErrorState title={network.group.loadFailed} message={usage.error.message} /> : null}
      {n ? (
        <div className="space-y-5">
          <section className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" aria-label={network.group.title}>
            <span>
              <span className="text-gray-500">{network.group.allowed}: </span>
              <strong>{network.group.requests(n.allowed, formatNumber(n.allowed))}</strong>
            </span>
            <span>
              <span className="text-gray-500">{network.group.blocked}: </span>
              <strong>{network.group.requests(n.blocked, formatNumber(n.blocked))}</strong>
            </span>
            <span>
              <span className="text-gray-500">{network.group.list}: </span>
              {n.network_list ? (
                <Link to={`/network/${n.network_list.id}`} className="font-medium text-primary hover:underline">
                  {n.network_list.name}
                </Link>
              ) : (
                <strong>{network.group.defaults}</strong>
              )}
            </span>
            {n.share_vm_network ? <span className="text-gray-700">{network.group.sharesVm}</span> : null}
          </section>
          {!n.network_list && n.blocked_domains.length > 0 ? (
            <NoListCallout groupId={groupId} groupName={name} blocked={n.blocked_domains.map((d) => d.domain)} owner={owner} />
          ) : null}
          <WidgetCard title={t.blocked} description={t.blockedHint}>
            {n.blocked_domains.length === 0 ? (
              <p className="m-0 text-sm text-gray-500">{t.noneBlocked}</p>
            ) : (
              <BlockedTable
                blocked={n.blocked_domains}
                allow={owner && n.network_list ? { listName: n.network_list.name, groups: list.data?.used_by.length ?? 1, onAllow: allow } : undefined}
              />
            )}
          </WidgetCard>
          <details className="rounded-xl border border-muted bg-gray-0 p-5 dark:bg-gray-50">
            <summary className="cursor-pointer text-base font-semibold text-gray-900">{t.allowedDomains(n.allowed_domains.length)}</summary>
            {n.allowed_domains.length === 0 ? (
              <p className="m-0 mt-3 text-sm text-gray-500">{t.noneAllowed}</p>
            ) : (
              <ul className="m-0 mt-3 list-none divide-y divide-muted/70 p-0">
                {n.allowed_domains.map((d) => (
                  <li key={d.domain} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                    <Mono className="min-w-0 whitespace-normal break-all text-[13px]">{d.domain}</Mono>
                    <span className="text-gray-500">{formatNumber(d.count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </details>
        </div>
      ) : null}
    </>
  );
}
